export class scribbleGenerator {
  constructor(container) {
    if (typeof container === "string") {
      container = document.querySelector(container);
    }
    if (!container) {
      throw new Error("Container element not found");
    }
    this.container = container;

    this.isMobile = window.innerWidth < 768;

    // デフォルト値を新しい仕様に合わせて更新 (1-100の割合)
    const defaults = {
      lineWidth: 6,
      complexity: 7,      // 複雑さ (1-10)
      maxLength: 50,      // 相対的な最大長 (1-100)
      strokeColor: "#000000",
      bounds: 0,          // 中央への重力 (0-10, 0=現在の動作, 10=最大の重力)
      speed: 50,          // 相対的なスピード (1-100)
      continuous: false
    };

    this.opts = { ...defaults };
    this.applyDataAttributes();

    this.canvas = container.querySelector("canvas") || document.createElement("canvas");
    if (!this.canvas.parentNode) {
      container.appendChild(this.canvas);
    }
    this.ctx = this.canvas.getContext("2d");

    this.pathSegments = [];
    this.currentX = 0;
    this.currentY = 0;
    this.segmentProgress = 0;
    this.tailProgress = 0;
    this.lastFrameTime = null;
    this.animationId = null;
    this.isPaused = false;

    // 計算されたオプション値を保持するプロパティ
    this.calculatedMaxLength = 0;

    this.init();
  }

  applyDataAttributes() {
    const prefix = "data-scribble-";

    Object.keys(this.opts).forEach(key => {
      const desktopAttr = this.container.getAttribute(`${prefix}${this.camelToKebab(key)}`);
      const mobileAttr = this.container.getAttribute(`${prefix}${this.camelToKebab(key)}-mobile`);

      if (this.isMobile) {
        if (mobileAttr !== null) {
          this.opts[key] = this.parseValue(mobileAttr);
        } else if (desktopAttr !== null) {
          this.opts[key] = this.parseValue(desktopAttr);
        }
      } else {
        if (desktopAttr !== null) {
          this.opts[key] = this.parseValue(desktopAttr);
        }
      }
    });
  }

  camelToKebab(str) {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  parseValue(value) {
    if (value === "true") return true;
    if (value === "false") return false;

    if (!isNaN(parseFloat(value)) && isFinite(value)) {
      return parseFloat(value);
    }
    return value;
  }

  adjustCanvasSize() {
    const rect = this.container.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.canvas.style.width = width + "px";
    this.canvas.style.height = height + "px";

    return { width, height };
  }
  
  /**
   * [NEW] 相対的なオプション値から、アニメーションで使う具体的な値を計算します。
   * このメソッドは init() と resize() で呼び出されます。
   */
  calculateDynamicOptions() {
    const { width, height } = this.adjustCanvasSize();
    
    // maxLength: 1-100の割合から、実際のセグメント数を計算
    // コンテナのサイズに比例させることで、どの大きさの要素でも見た目の密度が近くなる
    const baseSegments = (width + height) / 12; // 係数は見た目で調整
    const maxLengthRatio = this.opts.maxLength / 100;
    
    // 最低でも2セグメント (moveTo + 1つのカーブ) は必要
    this.calculatedMaxLength = Math.max(2, Math.floor(baseSegments * maxLengthRatio));
  }


  init() {
    this.calculateDynamicOptions();
    
    const { width, height } = { width: this.canvas.width / (window.devicePixelRatio||1), height: this.canvas.height / (window.devicePixelRatio||1) };

    // より広い範囲から開始位置を選択
    const padding = 40;
    this.currentX = padding + Math.random() * (width - padding * 2);
    this.currentY = padding + Math.random() * (height - padding * 2);

    this.pathSegments.push({
      type: "moveTo",
      x: this.currentX,
      y: this.currentY
    });

    this.animate(performance.now());
  }

  generateNextSegment() {
    const { width, height } = { width: this.canvas.width / (window.devicePixelRatio||1), height: this.canvas.height / (window.devicePixelRatio||1) };
    const random = (min, max) => Math.random() * (max - min) + min;

    // complexity: 1-10の値を正規化して使用
    const complexity = Math.max(1, Math.min(10, this.opts.complexity));
    const normalizedComplexity = (complexity - 1) / 9;
    const baseScale = Math.min(width / 200, height / 150);

    const controlRange = baseScale * (20 + normalizedComplexity * 60);
    const moveRange = baseScale * (30 + normalizedComplexity * 70);

    const aspectRatio = width / height;
    const xMult = aspectRatio > 1 ? Math.sqrt(aspectRatio) : 1;
    const yMult = aspectRatio < 1 ? Math.sqrt(1 / aspectRatio) : 1;

    // bounds: 0-10の値を正規化して重力の強さを決定
    const bounds = Math.max(0, Math.min(10, this.opts.bounds));
    const normalizedBounds = bounds / 10;
    const centerX = width / 2;
    const centerY = height / 2;

    let cp1x, cp1y, cp2x, cp2y, nextX, nextY;

    if (complexity <= 2) {
      const angle = random(0, Math.PI * 2);
      const distance = moveRange;

      nextX = this.currentX + Math.cos(angle) * distance * xMult;
      nextY = this.currentY + Math.sin(angle) * distance * yMult;

      const t1 = 0.33, t2 = 0.67;
      cp1x = this.currentX + (nextX - this.currentX) * t1;
      cp1y = this.currentY + (nextY - this.currentY) * t1;
      cp2x = this.currentX + (nextX - this.currentX) * t2;
      cp2y = this.currentY + (nextY - this.currentY) * t2;

      const wobble = controlRange * 0.1;
      cp1x += random(-wobble, wobble);
      cp1y += random(-wobble, wobble);
      cp2x += random(-wobble, wobble);
      cp2y += random(-wobble, wobble);
    } else {
      cp1x = this.currentX + random(-controlRange, controlRange) * xMult;
      cp1y = this.currentY + random(-controlRange, controlRange) * yMult;
      cp2x = this.currentX + random(-controlRange, controlRange) * xMult;
      cp2y = this.currentY + random(-controlRange, controlRange) * yMult;
      nextX = this.currentX + random(-moveRange, moveRange) * xMult;
      nextY = this.currentY + random(-moveRange, moveRange) * yMult;
    }

    // boundsに基づいて中央への重力を適用
    if (normalizedBounds > 0) {
      const distanceFromCenterX = this.currentX - centerX;
      const distanceFromCenterY = this.currentY - centerY;
      const distanceFromCenter = Math.sqrt(distanceFromCenterX * distanceFromCenterX + distanceFromCenterY * distanceFromCenterY);
      const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);
      const normalizedDistance = distanceFromCenter / maxDistance;
      
      // 中心から離れるほど強い重力をかける
      const gravityStrength = normalizedBounds * normalizedDistance * 0.5;
      
      // 次の位置を中央に向けて調整
      nextX -= distanceFromCenterX * gravityStrength;
      nextY -= distanceFromCenterY * gravityStrength;
      
      // コントロールポイントも中央に向けて調整（より自然な曲線のため）
      const cpGravity = gravityStrength * 0.6;
      cp1x -= (cp1x - centerX) * cpGravity * 0.3;
      cp1y -= (cp1y - centerY) * cpGravity * 0.3;
      cp2x -= (cp2x - centerX) * cpGravity * 0.3;
      cp2y -= (cp2y - centerY) * cpGravity * 0.3;
    }

    // 端に近い場合、内側に向かう力を自然に加える
    const edgeZone = 40; // エッジゾーンの幅
    const edgeForce = 0.7; // エッジでの反発力
    
    // X軸の調整
    if (nextX < edgeZone) {
      const distance = edgeZone - nextX;
      const force = (distance / edgeZone) * edgeForce;
      nextX += moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2x += moveRange * force * 0.3;
    } else if (nextX > width - edgeZone) {
      const distance = nextX - (width - edgeZone);
      const force = (distance / edgeZone) * edgeForce;
      nextX -= moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2x -= moveRange * force * 0.3;
    }
    
    // Y軸の調整
    if (nextY < edgeZone) {
      const distance = edgeZone - nextY;
      const force = (distance / edgeZone) * edgeForce;
      nextY += moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2y += moveRange * force * 0.3;
    } else if (nextY > height - edgeZone) {
      const distance = nextY - (height - edgeZone);
      const force = (distance / edgeZone) * edgeForce;
      nextY -= moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2y -= moveRange * force * 0.3;
    }
    
    // 最終的に安全な範囲内に収める（ソフトな制限）
    const safeMargin = 10;
    nextX = Math.max(safeMargin, Math.min(width - safeMargin, nextX));
    nextY = Math.max(safeMargin, Math.min(height - safeMargin, nextY));

    return {
      type: "bezierCurveTo",
      cp1x, cp1y, cp2x, cp2y,
      x: nextX, y: nextY
    };
  }

  getCubicPoint(ax, ay, bx, by, cx, cy, dx, dy, t) {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;
    return {
      x: ax * mt2 * mt + 3 * bx * mt2 * t + 3 * cx * mt * t2 + dx * t2 * t,
      y: ay * mt2 * mt + 3 * by * mt2 * t + 3 * cy * mt * t2 + dy * t2 * t
    };
  }

  drawPath() {
    const { width, height } = { width: this.canvas.width / (window.devicePixelRatio||1), height: this.canvas.height / (window.devicePixelRatio||1) };

    this.ctx.clearRect(0, 0, width, height);
    this.ctx.lineWidth = Math.min(this.opts.lineWidth, 50);
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
    this.ctx.strokeStyle = this.opts.strokeColor;

    this.ctx.beginPath();

    if (this.pathSegments.length < 2) {
      this.ctx.stroke();
      return;
    }

    const firstCurve = this.pathSegments[1];
    const startingPoint = this.tailProgress > 0 ?
      this.getCubicPoint(
        this.pathSegments[0].x, this.pathSegments[0].y,
        firstCurve.cp1x, firstCurve.cp1y,
        firstCurve.cp2x, firstCurve.cp2y,
        firstCurve.x, firstCurve.y,
        this.tailProgress
      ) :
      this.pathSegments[0];
    
    this.ctx.moveTo(startingPoint.x, startingPoint.y);

    for (let i = 1; i < this.pathSegments.length; i++) {
      const seg = this.pathSegments[i];
      const prevSeg = this.pathSegments[i - 1];
      const prevX = prevSeg.x;
      const prevY = prevSeg.y;

      const isFirstCurve = i === 1;
      const isLastCurve = i === this.pathSegments.length - 1;

      if (isFirstCurve && this.tailProgress > 0) {
        const steps = 30;
        const startT = this.tailProgress;
        for (let j = 1; j <= steps; j++) {
          const t = startT + (1 - startT) * (j / steps);
          const point = this.getCubicPoint(prevX, prevY, seg.cp1x, seg.cp1y, seg.cp2x, seg.cp2y, seg.x, seg.y, t);
          this.ctx.lineTo(point.x, point.y);
        }
      }
      else if (isLastCurve && this.segmentProgress < 1) {
        const steps = 30;
        const endT = this.segmentProgress;
        for (let j = 1; j <= steps; j++) {
          const t = endT * (j / steps);
          const point = this.getCubicPoint(prevX, prevY, seg.cp1x, seg.cp1y, seg.cp2x, seg.cp2y, seg.x, seg.y, t);
          this.ctx.lineTo(point.x, point.y);
        }
      }
      else {
        this.ctx.bezierCurveTo(seg.cp1x, seg.cp1y, seg.cp2x, seg.cp2y, seg.x, seg.y);
      }
    }

    this.ctx.stroke();
  }

  animate = (currentTime) => {
    if (this.isPaused) return;
    
    if (!this.lastFrameTime) {
      this.lastFrameTime = currentTime;
    }

    const deltaTime = currentTime - this.lastFrameTime;
    this.lastFrameTime = currentTime;

    // speed: 1-100の割合からアニメーションの増分を計算
    const normalizedSpeed = (Math.max(1, Math.min(100, this.opts.speed)) - 1) / 99; // 0 to 1
    const speedMultiplier = 0.2 + normalizedSpeed * 2.8; // 0.2から3.0の範囲にマッピング
    const increment = (deltaTime / 1000) * speedMultiplier;

    this.segmentProgress += increment;

    if (this.opts.continuous) {
      // maxLengthは計算済みの this.calculatedMaxLength を使用
      if (this.pathSegments.length > this.calculatedMaxLength) {
        this.tailProgress += increment;

        while (this.tailProgress >= 1) {
          if (this.pathSegments.length <= 2) break;
          this.tailProgress -= 1;
          const endOfCurve1 = this.pathSegments[1];
          this.pathSegments.splice(0, 2);
          this.pathSegments.unshift({
            type: 'moveTo',
            x: endOfCurve1.x,
            y: endOfCurve1.y
          });
        }
      }
    }

    if (this.segmentProgress >= 1) {
      this.segmentProgress -= 1;

      // maxLengthは計算済みの this.calculatedMaxLength を使用
      if (!this.opts.continuous && this.pathSegments.length >= this.calculatedMaxLength) {
        if (this.animationId) {
          cancelAnimationFrame(this.animationId);
          this.animationId = null;
        }
        return;
      }

      if (this.pathSegments.length > 0) {
        const lastSeg = this.pathSegments[this.pathSegments.length - 1];
        this.currentX = lastSeg.x;
        this.currentY = lastSeg.y;
      }

      this.pathSegments.push(this.generateNextSegment());
    }

    this.drawPath();
    if (!this.isPaused) {
      this.animationId = requestAnimationFrame(this.animate);
    }
  }

  resize() {
    this.isMobile = window.innerWidth < 768;
    this.applyDataAttributes();
    // サイズ変更時にオプションを再計算
    this.calculateDynamicOptions();
    this.drawPath();
  }

  pause() {
    this.isPaused = true;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
  
  restart() {
    this.isPaused = false;
    // アニメーションをリセット
    this.pathSegments = [];
    this.segmentProgress = 0;
    this.tailProgress = 0;
    this.lastFrameTime = null;
    
    // 再初期化
    this.init();
  }
  
  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}